import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Search, Edit, Trash2, File, Settings, Code } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import DocumentTemplatesService from '@/api/documentTemplates.service';
import DocumentTypesService from '@/api/documentTypes.service';
import { DataTable } from "@/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DocumentTemplate {
  id: number;
  publicId: string;
  documentTypeId: number;
  businessId?: number;
  name: string;
  extractionRules: {
    fields: string[];
    required: string[];
    optional?: string[];
  };
  validationRules: {
    amount_validation?: { min: number; max: number };
    date_validation?: { format: string };
    required_fields?: string[];
  };
  isActive: boolean;
  createdBy?: number;
  dateCreated: string;
  documentType?: {
    id: number;
    name: string;
  };
}

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

export default function DocumentTemplates() {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    documentTypeId: '',
    businessId: '',
    extractionFields: '',
    requiredFields: '',
    optionalFields: '',
    minAmount: '',
    maxAmount: '',
    dateFormat: 'dd/mm/yyyy',
    isActive: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesResponse, typesResponse] = await Promise.all([
        DocumentTemplatesService.getDocumentTemplates(selectedBusiness?.id),
        DocumentTypesService.getDocumentTypes()
      ]);

      if (templatesResponse.data.result === 'success') {
        setTemplates(templatesResponse.data.documentTemplates || []);
      }

      if (typesResponse.data.result === 'success') {
        setDocumentTypes(typesResponse.data.documentTypes || []);
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch templates and document types",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.documentTypeId) {
      toast({
        title: "Validation Error",
        description: "Template name and document type are required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      const extractionFields = formData.extractionFields.split(',').map(f => f.trim()).filter(f => f);
      const requiredFields = formData.requiredFields.split(',').map(f => f.trim()).filter(f => f);
      const optionalFields = formData.optionalFields.split(',').map(f => f.trim()).filter(f => f);

      const templateData = {
        name: formData.name,
        documentTypeId: parseInt(formData.documentTypeId),
        businessId: selectedBusiness?.id || null,
        extractionRules: {
          fields: extractionFields,
          required: requiredFields,
          optional: optionalFields
        },
        validationRules: {
          ...(formData.minAmount || formData.maxAmount ? {
            amount_validation: {
              min: parseFloat(formData.minAmount) || 0,
              max: parseFloat(formData.maxAmount) || 1000000
            }
          } : {}),
          date_validation: { format: formData.dateFormat },
          required_fields: requiredFields
        },
        isActive: formData.isActive
      };

      const response = await DocumentTemplatesService.createDocumentTemplate(templateData);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Document template created successfully",
        });
        setIsCreateDialogOpen(false);
        resetForm();
        fetchData();
      }
    } catch (error: any) {
      console.error('Error creating template:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedTemplate || !formData.name.trim() || !formData.documentTypeId) {
      toast({
        title: "Validation Error",
        description: "Template name and document type are required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      const extractionFields = formData.extractionFields.split(',').map(f => f.trim()).filter(f => f);
      const requiredFields = formData.requiredFields.split(',').map(f => f.trim()).filter(f => f);
      const optionalFields = formData.optionalFields.split(',').map(f => f.trim()).filter(f => f);

      const templateData = {
        name: formData.name,
        documentTypeId: parseInt(formData.documentTypeId),
        businessId: selectedBusiness?.id || null,
        extractionRules: {
          fields: extractionFields,
          required: requiredFields,
          optional: optionalFields
        },
        validationRules: {
          ...(formData.minAmount || formData.maxAmount ? {
            amount_validation: {
              min: parseFloat(formData.minAmount) || 0,
              max: parseFloat(formData.maxAmount) || 1000000
            }
          } : {}),
          date_validation: { format: formData.dateFormat },
          required_fields: requiredFields
        },
        isActive: formData.isActive
      };

      const response = await DocumentTemplatesService.updateDocumentTemplate(selectedTemplate.id, templateData);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Document template updated successfully",
        });
        setIsEditDialogOpen(false);
        setSelectedTemplate(null);
        resetForm();
        fetchData();
      }
    } catch (error: any) {
      console.error('Error updating template:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    try {
      setIsSubmitting(true);
      const response = await DocumentTemplatesService.deleteDocumentTemplate(selectedTemplate.id);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Document template deleted successfully",
        });
        setIsDeleteDialogOpen(false);
        setSelectedTemplate(null);
        fetchData();
      }
    } catch (error: any) {
      console.error('Error deleting template:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      documentTypeId: '',
      businessId: '',
      extractionFields: '',
      requiredFields: '',
      optionalFields: '',
      minAmount: '',
      maxAmount: '',
      dateFormat: 'dd/mm/yyyy',
      isActive: true
    });
  };

  const openEditDialog = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      documentTypeId: template.documentTypeId.toString(),
      businessId: template.businessId?.toString() || '',
      extractionFields: template.extractionRules.fields.join(', '),
      requiredFields: template.extractionRules.required.join(', '),
      optionalFields: template.extractionRules.optional?.join(', ') || '',
      minAmount: template.validationRules.amount_validation?.min?.toString() || '',
      maxAmount: template.validationRules.amount_validation?.max?.toString() || '',
      dateFormat: template.validationRules.date_validation?.format || 'dd/mm/yyyy',
      isActive: template.isActive
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedDocumentType === "all" || template.documentTypeId.toString() === selectedDocumentType;
    return matchesSearch && matchesType;
  });

  const columns = [
    {
      accessorKey: "name",
      header: "Template Name",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <File className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.getValue("name")}</span>
        </div>
      ),
    },
    {
      accessorKey: "documentType",
      header: "Document Type",
      cell: ({ row }: any) => {
        const template = row.original;
        const docType = documentTypes.find(dt => dt.id === template.documentTypeId);
        return (
          <Badge variant="outline">
            {docType?.name || 'Unknown'}
          </Badge>
        );
      },
    },
    {
      accessorKey: "businessId",
      header: "Scope",
      cell: ({ row }: any) => (
        <Badge variant={row.getValue("businessId") ? "default" : "secondary"}>
          {row.getValue("businessId") ? "Business" : "System"}
        </Badge>
      ),
    },
    {
      accessorKey: "extractionRules",
      header: "Fields",
      cell: ({ row }: any) => {
        const rules = row.getValue("extractionRules") as any;
        return (
          <span className="text-sm text-muted-foreground">
            {rules?.fields?.length || 0} fields
          </span>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }: any) => (
        <Badge variant={row.getValue("isActive") ? "default" : "secondary"}>
          {row.getValue("isActive") ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      accessorKey: "dateCreated",
      header: "Created",
      cell: ({ row }: any) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.getValue("dateCreated")).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => {
        const template = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEditDialog(template)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDeleteDialog(template)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const TemplateForm = ({ isEdit = false }) => (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="basic">Basic Info</TabsTrigger>
        <TabsTrigger value="extraction">Extraction Rules</TabsTrigger>
        <TabsTrigger value="validation">Validation Rules</TabsTrigger>
      </TabsList>
      
      <TabsContent value="basic" className="space-y-4">
        <div>
          <Label htmlFor="name">Template Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Standard Invoice Template"
          />
        </div>
        <div>
          <Label htmlFor="documentTypeId">Document Type</Label>
          <Select
            value={formData.documentTypeId}
            onValueChange={(value) => setFormData({ ...formData, documentTypeId: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select document type" />
            </SelectTrigger>
            <SelectContent>
              {documentTypes.map((type) => (
                <SelectItem key={type.id} value={type.id.toString()}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            id="isActive"
            checked={formData.isActive}
            onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
          />
          <Label htmlFor="isActive">Active</Label>
        </div>
      </TabsContent>
      
      <TabsContent value="extraction" className="space-y-4">
        <div>
          <Label htmlFor="extractionFields">Extraction Fields</Label>
          <Textarea
            id="extractionFields"
            value={formData.extractionFields}
            onChange={(e) => setFormData({ ...formData, extractionFields: e.target.value })}
            placeholder="vendorName, invoiceNumber, invoiceDate, totalAmount, taxAmount"
            className="h-20"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Comma-separated list of fields to extract from documents
          </p>
        </div>
        <div>
          <Label htmlFor="requiredFields">Required Fields</Label>
          <Textarea
            id="requiredFields"
            value={formData.requiredFields}
            onChange={(e) => setFormData({ ...formData, requiredFields: e.target.value })}
            placeholder="vendorName, totalAmount"
            className="h-16"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Fields that must be present for successful processing
          </p>
        </div>
        <div>
          <Label htmlFor="optionalFields">Optional Fields</Label>
          <Textarea
            id="optionalFields"
            value={formData.optionalFields}
            onChange={(e) => setFormData({ ...formData, optionalFields: e.target.value })}
            placeholder="taxAmount, paymentMethod, notes"
            className="h-16"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Fields that are nice to have but not required
          </p>
        </div>
      </TabsContent>
      
      <TabsContent value="validation" className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="minAmount">Minimum Amount</Label>
            <Input
              id="minAmount"
              type="number"
              value={formData.minAmount}
              onChange={(e) => setFormData({ ...formData, minAmount: e.target.value })}
              placeholder="0"
            />
          </div>
          <div>
            <Label htmlFor="maxAmount">Maximum Amount</Label>
            <Input
              id="maxAmount"
              type="number"
              value={formData.maxAmount}
              onChange={(e) => setFormData({ ...formData, maxAmount: e.target.value })}
              placeholder="1000000"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="dateFormat">Date Format</Label>
          <Select
            value={formData.dateFormat}
            onValueChange={(value) => setFormData({ ...formData, dateFormat: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dd/mm/yyyy">DD/MM/YYYY</SelectItem>
              <SelectItem value="mm/dd/yyyy">MM/DD/YYYY</SelectItem>
              <SelectItem value="yyyy-mm-dd">YYYY-MM-DD</SelectItem>
              <SelectItem value="dd-mm-yyyy">DD-MM-YYYY</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Document Templates</h1>
          <p className="text-muted-foreground">
            Configure AI extraction and validation rules for different document types
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Document Template</DialogTitle>
              <DialogDescription>
                Configure extraction and validation rules for AI document processing.
              </DialogDescription>
            </DialogHeader>
            <TemplateForm />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetForm();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedDocumentType} onValueChange={setSelectedDocumentType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {documentTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id.toString()}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Document Templates ({filteredTemplates.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredTemplates}
              section="document-templates"
              isLoading
            />
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Document Template</DialogTitle>
            <DialogDescription>
              Update the template configuration and rules.
            </DialogDescription>
          </DialogHeader>
          <TemplateForm isEdit={true} />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedTemplate(null);
                resetForm();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the template
              "{selectedTemplate?.name}" and may affect document processing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 